import { Grid, Typography } from "@mui/material";
import React, { useEffect, useRef } from "react";
import { motion, useInView, useMotionValue, useTransform, animate } from "framer-motion";

const skills = [
  { label: "ReactJS / Redux", percentage: 90 },
  { label: "HTML & CSS", percentage: 85 },
  { label: "JavaScript", percentage: 80 },
  { label: "GitHub", percentage: 72 },
  { label: "Node.js", percentage: 50 },
];

// ─────────────────────────────────────────────────────────────────────────────
// Circular SVG gauge — arc fills clockwise, center counter animates
// ─────────────────────────────────────────────────────────────────────────────
const SkillGauge = ({ label, percentage, index }) => {
  const R = 48;
  const C = 2 * Math.PI * R; // full circumference
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  const count = useMotionValue(0);
  const displayed = useTransform(count, (v) => `${Math.round(v)}%`);

  useEffect(() => {
    if (!inView) return;
    const controls = animate(count, percentage, {
      duration: 1.45,
      delay: index * 0.12,
      ease: 'easeOut',
    });
    return () => controls.stop();
  }, [inView, percentage, index, count]);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, scale: 0.78 }}
      animate={inView ? { opacity: 1, scale: 1 } : {}}
      transition={{ duration: 0.6, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: 140,
      }}
    >
      {/* SVG gauge ring */}
      <div style={{ position: 'relative', width: 120, height: 120 }}>
        <svg width="120" height="120" viewBox="0 0 120 120">
          {/* Track ring */}
          <circle
            cx="60" cy="60" r={R}
            fill="none"
            stroke="rgba(128,128,128,0.15)"
            strokeWidth="7"
          />
          {/* Animated fill arc — <g> rotates start to 12 o'clock */}
          <g transform="rotate(-90 60 60)">
            <motion.circle
              cx="60" cy="60" r={R}
              fill="none"
              stroke="#159895"
              strokeWidth="7"
              strokeLinecap="round"
              strokeDasharray={C}
              initial={{ strokeDashoffset: C }}
              animate={inView ? { strokeDashoffset: C * (1 - percentage / 100) } : {}}
              transition={{ duration: 1.45, delay: index * 0.12, ease: [0.22, 1, 0.36, 1] }}
            />
          </g>
        </svg>

        {/* Centered percentage counter */}
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <motion.span
            style={{
              fontFamily: 'Poppins, sans-serif',
              fontSize: 16,
              fontWeight: 700,
              color: '#159895',
              lineHeight: 1,
            }}
          >
            {displayed}
          </motion.span>
        </div>
      </div>

      {/* Skill label */}
      <Typography
        fontFamily="Poppins"
        fontWeight={500}
        fontSize={12}
        sx={{ mt: 1.2, textAlign: 'center', opacity: 0.75, lineHeight: 1.35 }}
      >
        {label}
      </Typography>
    </motion.div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
const Skills = () => {
  return (
    <Grid
      minHeight="100vh"
      justifyContent="center"
      alignItems="center"
      display="flex"
      py={2}
      container
      component="div"
      id="skill"
    >
      {/* Section heading */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.5 }}
        transition={{ duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
        style={{ width: '100%' }}
      >
        <Grid
          item
          style={{ borderRadius: '0px 40px 0px 40px' }}
          mt={10}
          alignItems="center"
          justifyContent="center"
          display="flex"
          xs={12}
          mb={5}
          border="solid 1px"
        >
          <Typography fontFamily="Alkatra" fontSize={30} fontWeight={700}>
            Skills
          </Typography>
        </Grid>
      </motion.div>

      {/* Gauge grid — flex-wrap, centered */}
      <Grid
        item
        xs={12}
        display="flex"
        flexWrap="wrap"
        gap={{ xs: 3, md: 5 }}
        justifyContent="center"
        pb={6}
        px={{ xs: 2, md: 4 }}
      >
        {skills.map((skill, i) => (
          <SkillGauge
            key={skill.label}
            label={skill.label}
            percentage={skill.percentage}
            index={i}
          />
        ))}
      </Grid>
    </Grid>
  );
};

export default Skills;
